import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../BBALoader';
import { Code, Eye, EyeOff, FileCode2, Info } from 'lucide-react';
import SaveButton from '../SaveButton';
import {
  JD_HTML_TOKENS,
  DEFAULT_JD_HTML_TEMPLATE,
  fetchJdHtmlTemplate,
  saveJdHtmlTemplate,
  renderJdHtmlTemplate,
} from '../../services/jobDescriptionHtmlTemplateService';

/** Sample values used by the preview iframe. */
const SAMPLE_VALUES = {
  logoUrl: '/bba-logo.png',
  title: 'Project Manager',
  department: 'Construction',
  companyName: 'Acme Remodeling Co.',
  primaryColor: '#C8102E',
  secondaryColor: '#1F2937',
  body: `
    <h2>Position Overview</h2>
    <p>The Project Manager owns every build from contract signing through final walkthrough, keeping projects on schedule, on budget, and up to standard.</p>
    <h2>Key Responsibilities</h2>
    <ul>
      <li>Build and maintain the master project schedule.</li>
      <li>Run weekly client meetings and publish updates.</li>
      <li>Manage subcontractor scopes, contracts, and quality.</li>
    </ul>
    <h2>Requirements</h2>
    <ul>
      <li>5+ years residential construction management.</li>
      <li>Working knowledge of project management software.</li>
    </ul>
  `,
};

/**
 * CRM ▸ Job Description Templates ▸ HTML Template tab. One master HTML wrapper
 * (with {{tokens}}) used when portal clients download any job description as a
 * PDF or Word doc. Leaving it blank keeps the portal's built-in layout.
 */
const JdHtmlTemplateEditor = ({ darkMode }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchJdHtmlTemplate()
      .then(({ html: saved }) => {
        if (!cancelled) setHtml(saved);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const missingRequired = useMemo(() => {
    if (!html.trim()) return [];
    return JD_HTML_TOKENS.filter(
      (t) => t.required && !new RegExp(`\\{\\{\\s*${t.token}\\s*\\}\\}`).test(html)
    ).map((t) => `{{${t.token}}}`);
  }, [html]);

  const previewSrcDoc = useMemo(() => {
    if (!showPreview) return '';
    const rendered = renderJdHtmlTemplate(html.trim() || DEFAULT_JD_HTML_TEMPLATE, SAMPLE_VALUES);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #111; background: #fff; margin: 0 auto; padding: 32px; max-width: 720px; }
      img { max-width: 100%; }
      h1, h2, h3 { line-height: 1.3; }
      ul { padding-left: 22px; }
    </style></head><body>${rendered}</body></html>`;
  }, [showPreview, html]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveJdHtmlTemplate(html.trim());
      setSavedAt(new Date());
    } catch (e) {
      console.error('Error saving Job Description HTML template:', e);
      alert('Error saving the HTML template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const card = `${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg`;
  const heading = darkMode ? 'text-white' : 'text-gray-900';
  const body = darkMode ? 'text-gray-300' : 'text-gray-600';
  const chip = `font-mono text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-800 text-red-300 border border-gray-600' : 'bg-white text-red-700 border border-gray-300'}`;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* How it works */}
      <div className={`${card} p-4 sm:p-6`}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <Info className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          <h3 className={`text-lg font-semibold text-center ${heading}`}>How the HTML Template Works</h3>
        </div>
        <ul className={`text-sm space-y-2 list-disc pl-5 ${body}`}>
          <li>This ONE template wraps <strong>every job description</strong> a client downloads from the portal.</li>
          <li>Write normal HTML with inline styles, and drop in <span className={chip}>{'{{tokens}}'}</span> where client data and the document content should go.</li>
          <li>When a client hits Download PDF or Download DOC, each token is swapped for their branding plus the document's title and content, then rendered.</li>
          <li>Leave the template <strong>blank</strong> to keep the portal's built-in layout.</li>
        </ul>
      </div>

      {/* Token reference */}
      <div className={`${card} p-4 sm:p-6`}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <Code className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          <h3 className={`text-lg font-semibold text-center ${heading}`}>Available Tokens</h3>
        </div>
        <div className="space-y-2">
          {JD_HTML_TOKENS.map((t) => (
            <div key={t.token} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-sm">
              <span className={`${chip} shrink-0`}>{`{{${t.token}}}`}</span>
              <span className={body}>
                <strong className={heading}>{t.label}</strong>
                {t.required && (
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>required</span>
                )}
                {' — '}{t.hint}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* HTML input */}
      <div className={`${card} p-4 sm:p-6`}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <FileCode2 className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          <h3 className={`text-lg font-semibold text-center ${heading}`}>HTML Template</h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <BBALoader size={32} />
          </div>
        ) : (
          <>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              spellCheck={false}
              placeholder={`Paste your HTML here, e.g.\n\n${DEFAULT_JD_HTML_TEMPLATE}`}
              className={`w-full h-96 font-mono text-xs leading-relaxed p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode
                  ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
            {missingRequired.length > 0 && (
              <p className={`mt-2 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                Heads up: the template is missing required token{missingRequired.length > 1 ? 's' : ''}{' '}
                {missingRequired.join(', ')} — downloads would leave that content out.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setHtml(DEFAULT_JD_HTML_TEMPLATE)}
                  className={`px-3 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Load Starter Template
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreview((p) => !p)}
                  className={`px-3 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-1.5 ${
                    darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPreview ? 'Hide Preview' : 'Preview with Sample Data'}
                </button>
              </div>
              <div className="flex items-center gap-3">
                {savedAt && !saving && (
                  <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    Saved {savedAt.toLocaleTimeString()}
                  </span>
                )}
                <SaveButton onClick={handleSave} variant="green" disabled={saving} title="Save HTML template" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Preview */}
      {showPreview && (
        <div className={`${card} p-4 sm:p-6`}>
          <h3 className={`text-lg font-semibold text-center mb-3 ${heading}`}>Preview (sample data)</h3>
          <iframe
            title="Job Description HTML template preview"
            srcDoc={previewSrcDoc}
            className="w-full h-[600px] rounded-lg border bg-white"
            sandbox=""
          />
        </div>
      )}
    </div>
  );
};

export default JdHtmlTemplateEditor;
